import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./DescriptionPreview.css";
import { cartUtils, wishlistUtils } from "../utils/userCartWishlistUtils";
import NoImage from "../../assets/no-image-icon-4.png";
import { showMyAlert } from "../../utils/alertService";

const DescriptionPreview = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [product] = useState(state?.product || {});
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [cartItemKey, setCartItemKey] = useState("");
  useEffect(() => {
    updateCartStatus();
    updateWishlistStatus();
  }, [product.id, selectedVariant]);

  const getAllImages = () => {
    const images = [];
    if (product.productImage) {
      images.push(`https://api.dremerz.net/uploads/${product.productImage}`);
    }
    for (let i = 1; i <= 4; i++) {
      const galleryImage = product[`galleryImage${i}`];
      if (galleryImage) {
        images.push(`https://api.dremerz.net/uploads/${galleryImage}`);
      }
    }
    return images;
  };

  const images = getAllImages();

  const updateCartStatus = () => {
    if (selectedVariant) {
      const key = cartUtils.findCartItemKey(product.id, selectedVariant);
      setCartItemKey(key || "");
      setCartCount(key ? cartUtils.getCart()[key].quantity : 0);
    } else {
      setCartCount(cartUtils.getProductCount(product.id));
    }
  };

  const updateWishlistStatus = () => {
    setIsInWishlist(wishlistUtils.isInWishlist(product.id));
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      showMyAlert({
        type: "warning",
        title: "Select Size",
        message: "Please select size first",
        duration: 2500,   
      })
      return;
    }

    const success = await cartUtils.addToCart(product.id, 1, selectedVariant);
    if (success) {
      updateCartStatus();
    } else {
      setShowLoginAlert(true);
    }
  };

  const handleRemoveFromCart = async () => {
    if (cartItemKey) {
      const success = await cartUtils.removeFromCart(cartItemKey);
      if (success) {
        updateCartStatus();
      }
    }
  };

  const handleIncreaseQuantity = async () => {
    if (cartItemKey) {
      const success = await cartUtils.updateQuantity(
        cartItemKey,
        cartCount + 1
      );
      if (success) {
        updateCartStatus();
      }
    }
  };

  const handleDecreaseQuantity = async () => {
    if (cartItemKey) {
      if (cartCount > 1) {
        const success = await cartUtils.updateQuantity(
          cartItemKey,
          cartCount - 1
        );
        if (success) {
          updateCartStatus();
        }
      } else {
        await handleRemoveFromCart();
      }
    }
  };

  const handleWishlistToggle = async () => {
    if (isInWishlist) {
      const success = await wishlistUtils.removeFromWishlist(product.id);
      if (success) {
        updateWishlistStatus();
      }
    } else {
      const success = await wishlistUtils.addToWishlist(product.id);
      if (success) {
        updateWishlistStatus();
      } else {
        setShowLoginAlert(true);
      }
    }
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
  };

  const handleImageClick = (imageUrl) => {
    const index = images.findIndex((img) => img === imageUrl);
    setCurrentImageIndex(index >= 0 ? index : 0);
    setModalImageUrl(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setModalImageUrl("");
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
    setModalImageUrl(images[nextIndex]);
  };

  const handlePrevImage = () => {
    const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
    setCurrentImageIndex(prevIndex);
    setModalImageUrl(images[prevIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showImageModal) {
        if (e.key === "ArrowRight") {
          handleNextImage();
        } else if (e.key === "ArrowLeft") {
          handlePrevImage();
        } else if (e.key === "Escape") {
          handleCloseModal();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showImageModal, currentImageIndex, images]);

  if (!product.id) {
    return <div className="loading-container">Loading product details...</div>;
  }

  let variants = [];
  try {
    variants = product.variants ? JSON.parse(product.variants) : [];
  } catch (e) {
    console.error("Error parsing variants", e);
  }
  const option1 = product.option1;
  const option2 = product.option2;
  const option3 = product.option3;

  return (
    <>
      <div className="product-detail-container">
        <div className="product-detail-grid">
          {/* Left Section: Main Product Image */}
          <div className="product-image-section">
            <div className="main-image-container">
              <img
                src={
                  product.productImage
                    ? `https://api.dremerz.net/uploads/${product.productImage}`
                    : NoImage
                }
                alt={product.productName}
                loading="lazy"
                className="main-product-image"
                onClick={() =>
                  handleImageClick(
                    `https://api.dremerz.net/uploads/${product.productImage}`
                  )
                }
              />
            </div>
          </div>

          {/* Middle Section: Product Details */}
          <div className="product-info-section">
            <div className="product-info-content">
              <h1 className="product-brand">{product.brandName}</h1>
              <h2 className="product-name">{product.productName}</h2>

              <div className="product-description">
                <p>{product.description}</p>
              </div>

              <ul className="product-features">
                <li>
                  <strong>
                    {option1.split(":")[0]}
                    {option1.includes(":") && ":"}
                  </strong>{" "}
                  {option1.split(":")[1]}
                </li>
                <li>
                  <strong>
                    {option2.split(":")[0]}
                    {option2.includes(":") && ":"}
                  </strong>{" "}
                  {option2.split(":")[1]}
                </li>
                <li>
                  <strong>
                    {option3.split(":")[0]}
                    {option3.includes(":") && ":"}
                  </strong>{" "}
                  {option3.split(":")[1]}
                </li>
              </ul>

              <div className="product-pricing">
                <span className="current-price">₹{product.sellingPrice}</span>
                {product.originalPrice && (
                  <span className="original-price-desview">
                    ₹{product.originalPrice}
                  </span>
                )}
              </div>

              {/* Size Selector - Moved up for better flow */}
              <div className="size-selector">
                <h3 className="size-selector-title">Select Size</h3>
                <div className="size-options">
                  {variants.length > 0 &&
                    variants.map((variant) =>
                      variant.stockQuantity > 0 ? (
                        <button
                          key={variant.variantUnit}
                          className={`size-option ${
                            selectedVariant === variant.variantUnit
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            handleVariantSelect(variant.variantUnit)
                          }
                        >
                          {variant.variantUnit}
                          <span className="stock-info"> available</span>
                        </button>
                      ) : (
                        <button
                          key={variant.variantUnit}
                          className={`size-option`}
                          disabled
                        >
                          {variant.variantUnit}
                          <span
                            style={{ color: "#920504" }}
                            className="stock-info"
                          >
                            Out of stock
                          </span>
                        </button>
                      )
                    )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="product-actions">
                {cartCount > 0 && selectedVariant ? (
                  <div className="quantity-controls  me-2 px-1">
                    <button
                      className="quantity-btn"
                      onClick={handleDecreaseQuantity}
                    >
                      -
                    </button>
                    <span className="quantity-display">{cartCount}</span>
                    <button
                      className="quantity-btn"
                      onClick={handleIncreaseQuantity}
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn cart-btn me-2"
                    style={{
                      backgroundColor: "#920504",
                      color: "white",
                      maxWidth: "max-content",
                    }}
                    onClick={handleAddToCart}
                    disabled={!selectedVariant}
                  >
                    <i className="fa fa-cart-plus"></i> Add to Cart
                  </button>
                )}

                <button
                  className={`btn wishlist-btn-desview ${
                    isInWishlist ? "in-wishlist" : ""
                  }`}
                  onClick={handleWishlistToggle}
                >
                  {isInWishlist ? "Remove ♥" : "Add ♡"}
                </button>
              </div>
            </div>
          </div>

          {/* Right Section: Gallery Images */}
          <div className="product-gallery-section">
            <div className="gallery-grid">
              {[1, 2, 3, 4].map(
                (index) =>
                  product[`galleryImage${index}`] && (
                    <div className="gallery-item" key={index}>
                      <img
                        src={`https://api.dremerz.net/uploads/${
                          product[`galleryImage${index}`]
                        }`}
                        loading="lazy"
                        alt={`Gallery ${index}`}
                        className="gallery-thumbnail"
                        onClick={() =>
                          handleImageClick(
                            `https://api.dremerz.net/uploads/${
                              product[`galleryImage${index}`]
                            }`
                          )
                        }
                      />
                    </div>
                  )
              )}
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {showImageModal && images.length > 0 && (
          <div className="image-modal-overlay" onClick={handleCloseModal}>
            <div
              className="image-modal-container"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-modal-btn" onClick={handleCloseModal}>
                &times;
              </button>

              {images.length > 1 && (
                <>
                  <button
                    className="modal-nav-btn modal-prev-btn"
                    onClick={handlePrevImage}
                  >
                    &#10094;
                  </button>
                  <button
                    className="modal-nav-btn modal-next-btn"
                    onClick={handleNextImage}
                  >
                    &#10095;
                  </button>
                </>
              )}

              <img
                src={modalImageUrl}
                loading="lazy"
                alt="Product Preview"
                className="modal-image"
              />

              {images.length > 1 && (
                <div className="image-counter">
                  {currentImageIndex + 1} / {images.length}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Login Alert Modal */}
        {showLoginAlert && (
          <div className="login-alert-overlay">
            <div className="login-alert-container">
              <h5>Dear User</h5>
              <p>Please login to continue</p>
              <div className="alert-actions">
                <button
                  className="btn cancel-btn"
                  onClick={() => setShowLoginAlert(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn login-btn"
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DescriptionPreview;
